import { useState, useEffect } from 'react';

// Hardcoded data
const usersData = [
  { 
    id: 1, 
    name: 'John Doe', 
    email: 'john.doe@company.com', 
    department: 'IT', 
    role: 'admin',
    status: 'active',
    lastLogin: '2023-10-18 09:45:22',
    createdAt: '2023-05-12',
  },
  { 
    id: 2, 
    name: 'Jane Smith', 
    email: 'jane.smith@company.com', 
    department: 'Finance', 
    role: 'user',
    status: 'active',
    lastLogin: '2023-10-17 16:22:10',
    createdAt: '2023-06-03',
  },
  { 
    id: 3, 
    name: 'Michael Chen', 
    email: 'michael.chen@company.com', 
    department: 'Marketing', 
    role: 'user',
    status: 'active',
    lastLogin: '2023-10-15 14:32:45',
    createdAt: '2023-07-18',
  },
  { 
    id: 4, 
    name: 'Sarah Johnson', 
    email: 'sarah.johnson@company.com', 
    department: 'HR', 
    role: 'user',
    status: 'active',
    lastLogin: '2023-10-18 10:15:33',
    createdAt: '2023-08-23',
  },
  { 
    id: 5, 
    name: 'Robert Wilson', 
    email: 'robert.wilson@company.com', 
    department: 'Operations', 
    role: 'user',
    status: 'inactive',
    lastLogin: '2023-09-05 11:12:08',
    createdAt: '2023-06-15',
  },
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  
  // Form state for adding/editing user
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    department: 'IT',
    role: 'user',
    status: 'active',
  });
  
  useEffect(() => {
    // Simulate API call to fetch users
    setTimeout(() => {
      setUsers(usersData);
      setIsLoading(false);
    }, 1000);
  }, []);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setUserForm({
      ...userForm,
      [name]: value,
    });
  };
  
  const handleAddUser = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!userForm.name || !userForm.email) {
      alert('Name and email are required');
      return;
    }
    
    // Simulate API call to add user
    const newUser = {
      id: users.length + 1,
      ...userForm,
      lastLogin: '-',
      createdAt: new Date().toISOString().split('T')[0],
    };
    
    setUsers([...users, newUser]);
    setIsAddingUser(false);
    
    // Reset form
    setUserForm({
      name: '',
      email: '',
      department: 'IT',
      role: 'user',
      status: 'active',
    });
    
    alert('User added successfully!');
  };
  
  const handleEditUserClick = (user) => {
    setEditingUserId(user.id);
    setUserForm({
      name: user.name,
      email: user.email,
      department: user.department,
      role: user.role,
      status: user.status,
    });
  };
  
  const handleUpdateUser = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!userForm.name || !userForm.email) {
      alert('Name and email are required');
      return;
    }
    
    // Simulate API call to update user
    const updatedUsers = users.map(user => {
      if (user.id === editingUserId) {
        return {
          ...user,
          ...userForm,
        };
      }
      return user;
    });
    
    setUsers(updatedUsers);
    setEditingUserId(null);
    
    // Reset form
    setUserForm({
      name: '',
      email: '',
      department: 'IT',
      role: 'user',
      status: 'active',
    });
    
    alert('User updated successfully!');
  };
  
  const cancelEdit = () => {
    setEditingUserId(null);
    setUserForm({
      name: '',
      email: '',
      department: 'IT',
      role: 'user',
      status: 'active',
    });
  };
  
  // Get unique departments for filter
  const departments = ['all', ...new Set(users.map(user => user.department))];
  
  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = departmentFilter === 'all' || user.department === departmentFilter;
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesDepartment && matchesRole;
  });
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          User Management
        </h1>
        <div>
          <button 
            onClick={() => setIsAddingUser(true)}
            className="btn btn-primary"
          >
            Add User
          </button>
        </div>
      </div>
      
      {/* Add User Form */}
      {isAddingUser && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New User</h2>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={userForm.name}
                  onChange={handleFormChange}
                  className="input mt-1"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={userForm.email}
                  onChange={handleFormChange}
                  className="input mt-1"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                  Department
                </label>
                <select
                  name="department"
                  id="department"
                  value={userForm.department}
                  onChange={handleFormChange}
                  className="input mt-1"
                >
                  <option value="IT">IT</option>
                  <option value="Finance">Finance</option>
                  <option value="Marketing">Marketing</option>
                  <option value="HR">HR</option>
                  <option value="Operations">Operations</option>
                  <option value="Sales">Sales</option>
                  <option value="Executive">Executive</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  name="role"
                  id="role"
                  value={userForm.role}
                  onChange={handleFormChange}
                  className="input mt-1"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsAddingUser(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                Add User
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* User Filters */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search Users
            </label>
            <input
              type="text"
              id="search"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={handleSearchChange}
              className="input"
            />
          </div>
          
          <div>
            <label htmlFor="departmentFilter" className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <select
              id="departmentFilter"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="input"
            >
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept === 'all' ? 'All Departments' : dept}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="roleFilter" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="roleFilter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Users List */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  {editingUserId === user.id ? (
                    <td colSpan="7" className="px-6 py-4">
                      <form onSubmit={handleUpdateUser} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                              Full Name
                            </label>
                            <input
                              type="text"
                              name="name"
                              id="edit-name"
                              value={userForm.name}
                              onChange={handleFormChange}
                              className="input mt-1"
                              required
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">
                              Email Address
                            </label>
                            <input
                              type="email"
                              name="email"
                              id="edit-email"
                              value={userForm.email}
                              onChange={handleFormChange}
                              className="input mt-1"
                              required
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="edit-department" className="block text-sm font-medium text-gray-700">
                              Department
                            </label>
                            <select
                              name="department"
                              id="edit-department"
                              value={userForm.department}
                              onChange={handleFormChange}
                              className="input mt-1"
                            >
                              <option value="IT">IT</option>
                              <option value="Finance">Finance</option>
                              <option value="Marketing">Marketing</option>
                              <option value="HR">HR</option>
                              <option value="Operations">Operations</option>
                              <option value="Sales">Sales</option>
                              <option value="Executive">Executive</option>
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700">
                              Role
                            </label>
                            <select
                              name="role"
                              id="edit-role"
                              value={userForm.role}
                              onChange={handleFormChange}
                              className="input mt-1"
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700">
                              Status
                            </label>
                            <select
                              name="status"
                              id="edit-status"
                              value={userForm.status}
                              onChange={handleFormChange}
                              className="input mt-1"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="btn btn-outline"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                          >
                            Save Changes
                          </button>
                        </div>
                      </form>
                    </td>
                  ) : (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{user.department}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                          ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                          ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{user.lastLogin}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => handleEditUserClick(user)}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                        >
                          Edit
                        </button>
                        <button className="text-red-600 hover:text-red-900">
                          {user.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No users found matching your search criteria.</p>
          </div>
        )}
      </div>
      
      {/* Bulk Actions */}
      <div className="mt-6 flex justify-between items-center">
        <div>
          <button className="btn btn-outline mr-2">
            Export Users
          </button>
          <button className="btn btn-outline">
            Bulk Import
          </button>
        </div>
        
        <div className="text-sm text-gray-500">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>
    </div>
  );
};

export default UserManagement; 